// Shared navigation configuration — single source of truth for Sidebar and TabBar.
//
// Sidebar renders NAV_GROUPS: seven semantic groups with Dashboard/Home first
// (analytics, sales, tasks, content, automation, telegram, admin), shown as
// collapsible submenus. The analytics group is intentionally first so the
// dashboard is the default landing and is always easy to reach. Item-level
// flags preserve the previous visibility rules (pro-mode, run-center,
// owner/admin role, platform admin, coming-soon placeholders).
// TabBar selects its own flat subset (TAB_NAV_ITEMS). Both share the
// isLinkActive function so active-state logic stays consistent.

import {
  LayoutDashboard,
  Kanban,
  CheckSquare,
  Users,
  Building2,
  FileText,
  Calendar,
  CalendarDays,
  ShieldCheck,
  Files,
  FileCode,
  Flame,
  Grid,
  Settings,
  Brain,
  Activity,
  Workflow,
  Terminal,
  BookOpen,
  Folder,
  FolderOpen,
  Bot,
  Code2,
  MessageSquare,
  Upload,
  MessagesSquare,
  RadioTower,
  Coins,
  CreditCard,
  History,
  Mail,
  TrendingUp,
  ListTodo,
  Layers,
  BarChart3,
  Zap,
  Send,
  Shield,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  badge?: string;
  /** Requires the pro-mode feature flag. */
  pro?: boolean;
  /** Requires the run-center feature flag. */
  runCenter?: boolean;
  /** Visible only to workspace owners and admins. */
  ownerAdminOnly?: boolean;
  /** Visible only to platform admins. */
  platformAdminOnly?: boolean;
  /** Placeholder route: rendered disabled with a coming-soon tooltip. */
  comingSoon?: boolean;
}

export interface NavGroup {
  /** Stable key used for the expand/collapse state. */
  key: string;
  labelKey: string;
  icon: LucideIcon;
  items: readonly NavItem[];
}

/** Items visibly active from `/dashboard` only when the path is exactly `/dashboard`. */
export function isLinkActive(pathname: string, to: string): boolean {
  if (to === '/dashboard') {
    return pathname === '/dashboard';
  }
  // '/admin' is the section landing: it must not stay highlighted on
  // '/admin/users' the same way '/dashboard' behaves for subpaths.
  if (to === '/admin') {
    return pathname === '/admin';
  }
  return pathname.startsWith(to);
}

// ─── Grouped navigation ────────────────────────────────────────────────────────
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    key: 'analytics',
    labelKey: 'crm.sidebar.group_analytics',
    icon: BarChart3,
    items: [
      { to: '/dashboard', labelKey: 'crm.nav.dashboard', icon: LayoutDashboard },
      { to: '/views', labelKey: 'crm.nav.views', icon: Grid },
    ],
  },
  {
    key: 'sales',
    labelKey: 'crm.sidebar.group_sales',
    icon: TrendingUp,
    items: [
      { to: '/deals', labelKey: 'crm.nav.deals', icon: Kanban },
      { to: '/hot-leads', labelKey: 'crm.nav.hot_leads', icon: Flame },
      { to: '/contacts', labelKey: 'crm.nav.contacts', icon: Users },
      { to: '/companies', labelKey: 'crm.nav.companies', icon: Building2 },
      { to: '/chat', labelKey: 'crm.nav.chat', icon: MessageSquare },
    ],
  },
  {
    key: 'tasks',
    labelKey: 'crm.sidebar.group_tasks',
    icon: ListTodo,
    items: [
      { to: '/projects', labelKey: 'crm.nav.projects', icon: FolderOpen },
      { to: '/tasks', labelKey: 'crm.nav.tasks', icon: CheckSquare },
      { to: '/calendar', labelKey: 'crm.nav.calendar', icon: CalendarDays },
      { to: '/meetings', labelKey: 'crm.nav.meetings', icon: Calendar },
    ],
  },
  {
    key: 'content',
    labelKey: 'crm.sidebar.group_content',
    icon: Layers,
    items: [
      { to: '/notes', labelKey: 'crm.nav.notes', icon: FileText },
      { to: '/documents', labelKey: 'crm.nav.documents', icon: Files },
      { to: '/templates', labelKey: 'crm.nav.templates', icon: FileCode },
      { to: '/imports', labelKey: 'crm.nav.imports', icon: Upload },
      { to: '/files', labelKey: 'crm.nav.files', icon: Folder, pro: true, comingSoon: true },
      {
        to: '/knowledge',
        labelKey: 'crm.nav.knowledge_base',
        icon: BookOpen,
        pro: true,
        comingSoon: true,
      },
    ],
  },
  {
    key: 'automation',
    labelKey: 'crm.sidebar.group_automation',
    icon: Zap,
    items: [
      { to: '/memory', labelKey: 'crm.nav.memory', icon: Brain, pro: true },
      { to: '/automations', labelKey: 'crm.nav.automations', icon: Workflow, pro: true },
      {
        to: '/run-center',
        labelKey: 'crm.nav.run_center',
        icon: Activity,
        badge: 'Beta',
        pro: true,
        runCenter: true,
      },
      {
        to: '/prompt-compiler',
        labelKey: 'crm.nav.prompt_compiler',
        icon: Terminal,
        pro: true,
        comingSoon: true,
      },
      { to: '/agents', labelKey: 'crm.nav.agents', icon: Bot, pro: true, comingSoon: true },
      { to: '/opencode', labelKey: 'crm.nav.opencode', icon: Code2, pro: true, comingSoon: true },
    ],
  },
  {
    key: 'telegram',
    labelKey: 'crm.sidebar.group_telegram',
    icon: Send,
    items: [
      {
        to: '/telegram-operations',
        labelKey: 'crm.nav.telegram_operations',
        icon: RadioTower,
        ownerAdminOnly: true,
      },
      { to: '/telegram-candidates', labelKey: 'crm.nav.telegram_candidates', icon: MessagesSquare },
      { to: '/approvals', labelKey: 'crm.nav.approvals', icon: ShieldCheck },
    ],
  },
  {
    key: 'admin',
    labelKey: 'crm.sidebar.group_admin',
    icon: Shield,
    items: [
      { to: '/team', labelKey: 'crm.nav.team', icon: Users },
      { to: '/settings', labelKey: 'crm.nav.settings', icon: Settings },
      {
        to: '/admin',
        labelKey: 'crm.admin.nav.dashboard',
        icon: LayoutDashboard,
        platformAdminOnly: true,
      },
      { to: '/admin/users', labelKey: 'crm.admin.nav.users', icon: Users, platformAdminOnly: true },
      {
        to: '/admin/workspaces',
        labelKey: 'crm.admin.nav.workspaces',
        icon: Building2,
        platformAdminOnly: true,
      },
      {
        to: '/admin/plans',
        labelKey: 'crm.admin.nav.plans',
        icon: CreditCard,
        platformAdminOnly: true,
      },
      { to: '/admin/llm', labelKey: 'crm.admin.nav.llm', icon: Coins, platformAdminOnly: true },
      { to: '/admin/mail', labelKey: 'crm.admin.nav.mail', icon: Mail, platformAdminOnly: true },
      {
        to: '/admin/audit',
        labelKey: 'crm.admin.nav.audit',
        icon: History,
        platformAdminOnly: true,
      },
    ],
  },
] as const;

// ─── TabBar subset ─────────────────────────────────────────────────────────────
/** Subset of nav items rendered by the TabBar component. */
export const TAB_NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'crm.nav.dashboard', icon: LayoutDashboard },
  { to: '/deals', labelKey: 'crm.nav.deals', icon: Kanban },
  { to: '/contacts', labelKey: 'crm.nav.contacts', icon: Users },
  { to: '/templates', labelKey: 'crm.nav.templates', icon: FileCode },
  { to: '/hot-leads', labelKey: 'crm.nav.hot_leads', icon: Flame },
  { to: '/settings', labelKey: 'crm.nav.settings', icon: Settings },
  { to: '/memory', labelKey: 'crm.nav.memory', icon: Brain },
] as const;
